import { createContext, useContext, useEffect, useCallback, ReactNode, useState } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanData } from './hooks/useFloorPlanData';
import { FloorPlan } from '@/types/floorPlanViewer';
import { useElementSizeDefaults } from '@/hooks/useElementSizeDefaults';
import type { ElementType } from '@/services/elementSizeDefaultsService';
import { logger } from '@/lib/logger';

interface BroadcastFunctions {
  broadcastDrag: (
    elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
    elementId: string,
    x: number,
    y: number,
  ) => Promise<void>;
  broadcastDragEnd: (
    elementType: 'fixture' | 'control' | 'product' | 'annotation' | 'room' | 'panel',
    elementId: string,
  ) => Promise<void>;
  broadcastCursor: (x: number, y: number) => Promise<void>;
  broadcastCursorLeave: () => Promise<void>;
}

interface FloorPlanContextValue {
  floorPlan: FloorPlan;
  data: ReturnType<typeof useFloorPlanData>;
  broadcastFunctions: BroadcastFunctions | null;
  getDefaultScale: (
    elementType: ElementType,
    category: string,
    subcategory?: string,
  ) => number | null;
  elementSizeDefaultsLoading: boolean;
  // Temporary image opacity for real-time slider feedback (null = use saved value)
  temporaryImageOpacity: number | null;
  setTemporaryImageOpacity: (opacity: number | null) => void;
  // Temporary product label font size for real-time slider feedback (null = use saved value)
  temporaryProductLabelFontSize: number | null;
  setTemporaryProductLabelFontSize: (fontSize: number | null) => void;
  // Version counter for category icons - increment to trigger icon re-fetch in ProductIcon
  categoryIconVersion: number;
  incrementCategoryIconVersion: () => void;
}

const FloorPlanContext = createContext<FloorPlanContextValue | null>(null);

interface FloorPlanProviderProps {
  children: ReactNode;
  floorPlan: FloorPlan;
  broadcastFunctions?: BroadcastFunctions | null;
}

export function FloorPlanProvider({
  children,
  floorPlan,
  broadcastFunctions = null,
}: FloorPlanProviderProps) {
  const setCurrentFloorPlan = useAppStore(state => state.setCurrentFloorPlan);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const setRoomVisibility = useAppStore(state => state.setRoomVisibility);
  const setMarkerOpacity = useAppStore(state => state.setMarkerOpacity);
  const data = useFloorPlanData(floorPlan.id);
  const { getDefaultScale: getOrgDefaultScale, isLoading: elementSizeDefaultsLoading } =
    useElementSizeDefaults();

  // Temporary image opacity for real-time slider feedback
  const [temporaryImageOpacity, setTemporaryImageOpacity] = useState<number | null>(null);
  // Temporary product label font size for real-time slider feedback
  const [temporaryProductLabelFontSize, setTemporaryProductLabelFontSize] = useState<number | null>(
    null,
  );
  // Version counter for category icons - increment to trigger icon re-fetch
  const [categoryIconVersion, setCategoryIconVersion] = useState(0);
  const incrementCategoryIconVersion = useCallback(() => {
    setCategoryIconVersion(v => v + 1);
  }, []);

  // Create a wrapped getDefaultScale that checks per-floor-plan defaults first,
  // then falls back to organization-wide defaults
  const getDefaultScale = useCallback(
    (elementType: ElementType, category: string, subcategory?: string): number | null => {
      // First, check per-floor-plan defaults (stored in floor_plan_metadata.marker_sizes.category_defaults)
      const perFloorPlanDefault = data.getCategoryDefault(elementType, category, subcategory);

      if (perFloorPlanDefault !== null) {
        logger.log('[FloorPlanProvider] Using per-floor-plan default:', {
          elementType,
          category,
          subcategory,
          scale: perFloorPlanDefault,
        });
        return perFloorPlanDefault;
      }

      // Fall back to organization-wide defaults
      const orgDefault = getOrgDefaultScale(elementType, category, subcategory);

      if (orgDefault !== null) {
        logger.log('[FloorPlanProvider] Using organization default:', {
          elementType,
          category,
          subcategory,
          scale: orgDefault,
        });
      }

      return orgDefault;
    },
    [data.getCategoryDefault, getOrgDefaultScale],
  );

  useEffect(() => {
    setCurrentFloorPlan(floorPlan.id);
    // Close any open drawer when changing floor plans
    setActiveDrawer(null);
    // Reset room visibility to OFF by default when opening a floor plan
    setRoomVisibility(false);
    // Reset marker opacity to 50% by default when opening a floor plan
    setMarkerOpacity(0.5);

    return () => {
      setCurrentFloorPlan(null);
      setActiveDrawer(null);
    };
  }, [floorPlan.id, setCurrentFloorPlan, setActiveDrawer, setRoomVisibility, setMarkerOpacity]);

  return (
    <FloorPlanContext.Provider
      value={{
        floorPlan,
        data,
        broadcastFunctions,
        getDefaultScale,
        elementSizeDefaultsLoading,
        temporaryImageOpacity,
        setTemporaryImageOpacity,
        temporaryProductLabelFontSize,
        setTemporaryProductLabelFontSize,
        categoryIconVersion,
        incrementCategoryIconVersion,
      }}
    >
      {children}
    </FloorPlanContext.Provider>
  );
}

export function useFloorPlanContext() {
  const context = useContext(FloorPlanContext);
  if (!context) {
    throw new Error('useFloorPlanContext must be used within FloorPlanProvider');
  }
  return context;
}
